import PropTypes from "prop-types";
import Icon from "@react-native-vector-icons/material-design-icons";
import theme from "assets/theme";
import React, { useMemo, useState } from "react";
import { StyleSheet, Text, TouchableWithoutFeedback, View } from "react-native";
import PressableContainer from "webComponentsFormElements/PressableContainer.component";

const classes = StyleSheet.create({
  selection: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  items: {
    flexDirection: "row",
    padding: 8,
    borderColor: theme.color.greyHighlight,
    justifyContent: "center",
  },
  selectContainer: {
    width: "100%",
  },
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "transparent",
    zIndex: 999,
  },
});

export default function Dropdown(props) {
  const { label, options, selected, onSelect, textStyle, backgroundColor, onOpenChange } =
    props;
  const [isOpen, setIsOpen] = useState(false);
  const [displaySelected, setDisplaySelected] = useState(selected);
  const [minLabelWidth, setMinLabelWidth] = useState(0);
  const background = backgroundColor || "white";
  const menuTopMargin = label ? 50 : 30;
  const stateClasses = useMemo(
    () =>
      StyleSheet.create({
        menu: {
          position: "absolute",
          zIndex: 1,
          top: menuTopMargin,
          minWidth: minLabelWidth,
          overflow: "hidden",
          borderRadius: 20,
          borderColor: theme.color.greyHighlight,
          borderWidth: 1,
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          alignSelf: "center",
          backgroundColor: background,
        },
        isOpen: {
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? "auto" : "none",
        },
        root: {
          minWidth: minLabelWidth,
          flexDirection: "column",
          backgroundColor: background,
          zIndex: 1000,
        },
      }),
    [minLabelWidth, isOpen, background, menuTopMargin],
  );
  return (
    <>
      {isOpen && (
        <TouchableWithoutFeedback onPress={() => {
          setIsOpen(false); onOpenChange?.(false);
        }}>
          <View style={classes.overlay} />
        </TouchableWithoutFeedback>
      )}
      <View style={stateClasses.root}>
        {label && <Text style={textStyle}>{label}:</Text>}
        <View style={classes.selectContainer}>
          <PressableContainer
            onPress={() => {
              const next = !isOpen; setIsOpen(next); onOpenChange?.(next);
            }}
            style={classes.selection}
          >
            <Text style={textStyle}>
              {options.find((option) => option.value === displaySelected)
                ?.label || "Select an option"}
            </Text>
            <Icon
              name={
                isOpen
                  ? "arrow-up-drop-circle-outline"
                  : "arrow-down-drop-circle-outline"
              }
              style={textStyle}
            />
          </PressableContainer>
        </View>
        <View style={[stateClasses.menu, stateClasses.isOpen]}>
          {options.map((option) => (
            <PressableContainer
              key={option.value}
              style={classes.items}
              onPress={() => {
                onSelect(option.value);
                setDisplaySelected(option.value);
                setIsOpen(false);
                onOpenChange?.(false);
              }}
            >
              <Text
                value={option.value}
                style={textStyle}
                onLayout={(event) => {
                  const { width } = event.nativeEvent.layout;
                  setMinLabelWidth((prev) => Math.max(prev, width + 45));
                }}
              >
                {option.label}
              </Text>
            </PressableContainer>
          ))}
        </View>
      </View>
    </>
  );
}
  Dropdown.propTypes = {
    label: PropTypes.string,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.any.isRequired,
        label: PropTypes.string.isRequired,
      })
    ).isRequired,
    selected: PropTypes.any,
    onSelect: PropTypes.func.isRequired,
    textStyle: PropTypes.object,
    backgroundColor: PropTypes.string,
    onOpenChange: PropTypes.func,
  };
